import { v4 as uuidv4 } from 'uuid';

import API from 'configs/API';
import { axiosGet, axiosPost } from 'utils/apis/axios';

const ACCEPTED_AUDIO_MIME = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/x-pn-wav',
  'audio/mp4',
  'audio/x-m4a',
  'audio/aac',
  'audio/m4a',
];

const useUploadAudioS3 = () => {
  const getS3PresignedUrl = async (audioKey) => {
    const { success, data } = await axiosGet(API.AWS.S3_PRESIGNED, {
      params: {
        key: audioKey,
      },
    });
    if (success) {
      return data;
    }
    return false;
  };

  const uploadAudioToS3 = async (s3PresignedData, uploadFile, { onUploadProgress } = {}) => {
    const formData = new FormData();
    Object.entries(s3PresignedData.fields).forEach(([k, v]) => {
      formData.append(k, v);
    });

    formData.append('file', uploadFile);

    let response = await axiosPost(s3PresignedData.url, formData, {
      transformRequest: (req, headers) => {
        delete headers.Authorization;
        return req;
      },
      onUploadProgress: (evt) => {
        if (onUploadProgress) onUploadProgress(evt);
      },
      withCredentials: false,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });

    // If axios failed due to CORS/network, try fetch fallback
    if (!response?.success) {
      try {
        const fetchResp = await fetch(s3PresignedData.url, {
          method: 'POST',
          body: formData,
          // Let browser set headers for multipart boundary
          credentials: 'omit',
        });
        const ok = fetchResp.status === 204;
        let bodyText = '';
        if (!ok) {
          try {
            bodyText = await fetchResp.text();
            // eslint-disable-next-line no-empty
          } catch (e) {}
        }
        return { success: ok, status: fetchResp.status, data: bodyText };
      } catch (e) {
        return { success: false, error: e, message: e?.message || 'Fetch fallback error' };
      }
    }

    return response;
  };

  const uploadAudio = async ({ audioKey, uploadFile, onUploadProgress } = {}) => {
    const s3PresignedData = await getS3PresignedUrl(audioKey);
    if (!s3PresignedData || !s3PresignedData.fields) {
      return { success: false };
    }
    const uploadResponse = await uploadAudioToS3(s3PresignedData, uploadFile, { onUploadProgress });
    return uploadResponse;
  };

  const generateAudioKey = (file) => {
    let ext = 'mp3';
    if (file?.name && file.name.includes('.')) {
      ext = file.name.split('.').pop();
    } else if (file?.type) {
      const [, subtype] = file.type.split('/');
      ext = subtype?.replace('x-wav', 'wav')?.replace('x-m4a', 'm4a') || 'mp3';
    }
    const key = `${uuidv4()}.${ext}`;
    return key;
  };

  const isAcceptedAudio = (file) => {
    if (!file) return false;
    if (file.type && ACCEPTED_AUDIO_MIME.includes(file.type)) return true;
    const name = file.name || '';
    return /(\.mp3|\.wav|\.m4a)$/i.test(name);
  };

  return { generateAudioKey, uploadAudio, isAcceptedAudio };
};

export default useUploadAudioS3;
